
import {translate} from '@elements/translations';
import LoadingSpinner from '../components/loading/LoadingSpinner.vue';
import FullscreenModal from "../components/modal/FullscreenModal.vue";
import StepNav from "../components/step-nav/StepNav.vue";
import Breadcrumb from "../components/breadcrumb/Breadcrumb.vue";
import {addParamsToUrl, assertData, throwAlertMessage} from "../utils/utils";
import {ActivityBaseConfigStruct} from "../utils/structs";
import {getActivityBaseConfig} from "../utils/config-helper";
import {ref, computed, reactive, watch, onMounted, defineComponent} from 'vue';
import {storeToRefs} from 'pinia'
import {useActivityStore} from '../stores/ActivityStore';
import CalendarView from "./activity-views/CalendarView.vue";
import DateListView from "./activity-views/DateListView.vue";
import TimeListView from "./activity-views/TimeListView.vue";
import { setCartCount } from '../../scripts/load-cart';
import {
    addParamsToUrl,
    assertData,
    fetcher,
    areEqualArrays,
    throwAlertMessage,
    convertInCurrency
} from "../utils/utils";
import SuccessView from "./ticket-views/SuccessView.vue";

export default defineComponent({
    name: "ActivityTicketConfig",
    components: {
        SuccessView,
        FullscreenModal,
        LoadingSpinner,
        StepNav,
        Breadcrumb,
        CalendarView,
        DateListView,
        TimeListView
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        activityId: {
            type: String,
            required: true,
        },
        firstView: {
            type: String,
            required: true,
        },
        isTimeList: {
            type: Boolean,
            required: false,
            default: false
        },
        isPersonsTimeList: {
            type: Boolean,
            required: false,
            default: false
        },
        buttonConfig: {
            type: Object,
            required: false,
            default: {
                text: translate('shop.configuration.add-to-cart'),
                class: "btn btn-primary",
                onlyIcon: false
            }
        },
    },
    setup(props) {
        const activityStore = useActivityStore();
        const {
            step,
            selectedDateStart,
            selectedDateEnd,
            stepValidation,
            ticketConfigurations
        } = storeToRefs(activityStore);
        const {changeStep, changeDateStart, changeDateEnd, changeStepValidation, resetAll} = activityStore;

        const isLoading = ref<boolean>(false);
        const successData = ref<Object>({});

        const stepViews = ref([]);

        const getBaseData = async () => {
            assertData(
                {
                    title: props.title,
                    activityId: props.activityId,
                    firstView: props.firstView,
                },
                ActivityBaseConfigStruct,
                "ActivityTicketConfig",
                "Config is not valid",
                "danger");

        }

        const steps = computed<number>(() => {
            let tempSteps = 1;

            if (props.firstView === 'calendar') {
                tempSteps++;
            }
            if (props.firstView === 'date-list') {
                tempSteps++;
            }
            return tempSteps;
        });

        const getStepViews = <array>() => {
            let views = [];

            views.push(props.firstView);
            if (props.firstView === 'calendar') {
                if (props.isTimeList || props.isPersonsTimeList) {
                    views.push('time-list');
                } else {
                    views.push('date-list');
                }
            }
            return views;
        };


        const onChangeStep = (step: number) => {
            changeStep(step);
        }

        const onChangeDateStart = (date: string) => {
            changeDateStart(date);
        }

        const onChangeDateEnd = (date: string) => {
            changeDateEnd(date);
        }

        const onValidateStep = () => {
            let view = stepViews.value[step.value];
            let validation = {
                view: view,
                validationError: translate('Shop.configuration.validation.' + view),
                isValid: () => onIsValid(view)
            }
            changeStepValidation(validation);
        }

        const onIsValid = (view) => {
            let isValid = false;
            switch (view) {
                case 'calendar':
                    isValid = (selectedDateEnd.value !== "");
                    break;
                case 'date-list':
                    let ticketCountDate = 0;
                    let configDate = ticketConfigurations.value;
                    Object.keys(configDate.priceGroupsPerDate).forEach((key) => {
                        Object.keys(configDate.priceGroupsPerDate[key]).forEach((priceGroup) => {
                            ticketCountDate += configDate.priceGroupsPerDate[key][priceGroup].counter;
                        });
                    });
                    isValid = ticketCountDate !== 0;
                    break;
                case 'time-list':
                    let ticketCountDateTime = 0;
                    let configDateTime = ticketConfigurations.value;
                    Object.keys(configDateTime.priceGroupsPerDateTime).forEach((key) => {
                        Object.keys(configDateTime.priceGroupsPerDateTime[key]).forEach((priceGroup) => {
                            ticketCountDateTime += configDateTime.priceGroupsPerDateTime[key][priceGroup].counter;
                        });
                    });
                    isValid = ticketCountDateTime !== 0;
                    break;
                case 'summary':
                    isValid = true;
                    break;
            }
            return isValid;
        }


        const onAddToCart = async () => {
            isLoading.value = true;

            const params = {
                activityId: props.activityId,
                configurations: []
            };

            ticketConfigurations.value.configurations.forEach((configuration, key) => {
            //To-Do
            console.log(configuration);
                let config = {
                    ...(configuration.selectedDate !== "" && {selectedDate: configuration.selectedDate}),
                    ...(configuration.selectedTime !== "" && {selectedTime: configuration.selectedTime}),
                    ...(configuration.amount !== "" && {amount: configuration.amount}),
                    ...(configuration.priceGroup !== "" && {priceGroup: configuration.priceGroup}),
                }
                params['configurations'][key] = config;
            });

            let url = getActivityBaseConfig().addToCart;
            let request = fetch(url, {
                method: 'POST',
                body: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            await request.then(result => result.json()).then(result => {
                isLoading.value = false;
                successData.value = result;
                if (result.cartItemsCounter) {
                    setCartCount(result.cartItemsCounter);
                }
            }).catch((error) => {
                console.log(error);
            });
        };


        const onCloseModal = () => {
            resetAll();
        }

        watch(() => [selectedDateStart.value, selectedDateEnd.value, ticketConfigurations.value, step.value], (option) => {
            onValidateStep();
        });

        onMounted(() => {
            getBaseData();
            stepViews.value = getStepViews();
            onValidateStep();
        });

        return {
            step,
            onChangeStep,
            onChangeDateStart,
            onChangeDateEnd,
            selectedDateStart,
            selectedDateEnd,
            steps,
            translate,
            stepViews,
            onAddToCart,
            successData,
            onCloseModal,
            isLoading,
        }
    }
});
